import { FilteredAccount } from '@interfaces/models/client-accounts.interface';
import { Action, createReducer, on } from '@ngrx/store';
import {
  IRAOptions,
  IRATypes,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
} from '@shared/models';
import * as AdaptedStateActions from '@store/actions/adapted-state/adapted-state.actions';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';

import * as openAccountsActions from '../../actions/open-account/open-account.actions';
import * as SelectedClientAccounts from '../../actions/selected-client-accounts/selected-client-accounts.actions';

export * from '@interfaces/models/selected-client-accounts.interface';

export const initialState: ISelectedClientAccountsState = {
  transferFrom: {
    selectedAccounts: [],
  },
  transferTo: {
    hasEligibleExistingAccount: false,
    iraOption: IRAOptions.UNKNOWN,
    iraType: IRATypes.UNKNOWN,
    selectedAccount: null,
  },
  transactionType: TransactionTypesEnum.UNKNOWN,
};

const selectedClientAccountsReducer = createReducer(
  initialState,
  on(SelectedClientAccounts.UpdateSelectedAccountsFrom, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedAccounts: payload,
    },
  })),
  on(SelectedClientAccounts.ClearSelectedAccount, () => {
    return initialState;
  }),
  on(AdaptedStateActions.ClearCurrentTransactionState, () => {
    return initialState;
  }),
  on(StartOverStateCleared, () => initialState),
  on(SelectedClientAccounts.UpdateSelectedAccountTo, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      selectedAccount: payload,
    },
  })),
  on(openAccountsActions.submitOpenAccountResponse, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      selectedAccount: {
        ...state.transferTo.selectedAccount,
        accountNumber: payload.brokerageAccountNumber,
        accountData: {
          accountId: payload.accountId,
          accountNumber: payload.brokerageAccountNumber,
        } as FilteredAccount,
      },
    },
  })),
  on(SelectedClientAccounts.UpdateTransactionType, (state, { payload }) => ({
    ...state,
    transactionType: payload,
  })),
  on(SelectedClientAccounts.UpdateIraType, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      iraType: payload,
    },
  })),
  on(SelectedClientAccounts.UpdateIraOption, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      iraOption: payload,
    },
  })),
  on(SelectedClientAccounts.UpdateHasEligibleExistingAccount, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      hasEligibleExistingAccount: payload,
    },
  })),
  on(AdaptedStateActions.SetTransactionAsCurrent, (state, { transaction }) => {
    return {
      ...state,
      ...transaction.selectedClientAccounts,
    };
  }),
);

export function reducer(state: ISelectedClientAccountsState | undefined, action: Action) {
  return selectedClientAccountsReducer(state, action);
}

export const getSelectedAccountsFrom = (state: ISelectedClientAccountsState) =>
  state ? state.transferFrom.selectedAccounts : null;
export const getSelectedAccountTo = (state: ISelectedClientAccountsState) =>
  state ? state.transferTo.selectedAccount : null;

export const getTransactionType = (state: ISelectedClientAccountsState) =>
  state ? state.transactionType : null;
export const getIraType = (state: ISelectedClientAccountsState) =>
  state ? state.transferTo.iraType : null;
export const getIraOption = (state: ISelectedClientAccountsState) =>
  state ? state.transferTo.iraOption : null;
export const getHasEligibleExistingAccount = (state: ISelectedClientAccountsState) =>
  state ? state.transferTo.hasEligibleExistingAccount : null;

export const getAsOfDateFromSelectedAccountsFrom = (state: ISelectedClientAccountsState) => {
  const selectedAccountsFromState = getSelectedAccountsFrom(state);
  return selectedAccountsFromState ? selectedAccountsFromState[0].accountData.dateDisplay : '';
};
